import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import BulkTimeAdjustmentModal from "../components/BulkTimeAdjustmentModal";
import DocumentFileButton from "../components/DocumentFileButton";
import PunchPhotoModal from "../components/PunchPhotoModal";
import ReasonModal from "../components/ReasonModal";
import TimeAdjustmentModal, { TimeAdjustmentModalMode } from "../components/TimeAdjustmentModal";
import {
  ADJUSTMENT_REASON_LABELS,
  ADJUSTMENT_STATUS_BADGE,
  ADJUSTMENT_STATUS_LABELS,
  ADJUSTMENT_TYPE_LABELS,
  annulAdjustmentApi,
  annulBatchApi,
  approveAdjustmentApi,
  approveManyAdjustmentsApi,
  closeTimesheetApi,
  DAY_FLAG_LABELS,
  DAY_KIND_LABELS,
  describeIssue,
  DpAdjustment,
  DpPunch,
  DpTimesheetDetail,
  duration,
  fetchAdjustmentFileApi,
  fetchTimesheetPdfApi,
  getTimesheetApi,
  hhmm,
  recomputeTimesheetApi,
  rejectAdjustmentApi,
  reopenTimesheetApi,
  SIGNATURE_BADGE,
  SIGNATURE_LABELS,
  timeOf,
  TimesheetBlockedError,
  TimesheetDay,
} from "../api/timekeepingApi";
import { DP_PAGE, DP_TIMESHEET_PAGE, DP_TIMESHEETS_PAGE } from "../routes";
import { formatCpf, formatDateOnly, MONTH_LABELS } from "../utils/format";
import "./Dp.css";
import "./DpEco.css";
import "./Timekeeping.css";

const REST = new Set(["FOLGA", "FERIADO", "LOJA_FECHADA", "FERIAS", "AFASTAMENTO", "SEM_ESCALA"]);
const INFO_FLAGS = new Set(["COM_AJUSTE", "SEM_ESCALA_PUBLICADA", "INTERVALO_PRESUMIDO", "TRABALHO_EM_DESCANSO", "FORA_DO_PERIODO"]);
const SHIFT: Record<string, string> = { MANHA: "Manhã", NOITE: "Noite" };
const key = (iso: string) => iso.slice(0, 10);
const weekdayDate = (iso: string) => new Date(iso).toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "short", day: "2-digit", month: "2-digit" });
const formatMeters = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1).replace(".", ",")} km` : `${m} m`);
const brDateTime = (iso: string) => new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" });

type Reason =
  | { kind: "reject"; adjustment: DpAdjustment }
  | { kind: "annul"; adjustment: DpAdjustment }
  | { kind: "annulBatch"; batchId: string; count: number }
  | { kind: "reopen" };

/** Descreve o que um ajuste faz, em uma linha. */
function describeAdjustment(a: DpAdjustment) {
  if (a.type === "INCLUSAO_MARCACAO" && a.proposedAt) return `Incluir ${timeOf(a.proposedAt)}`;
  if (a.type === "DESCONSIDERAR_MARCACAO") return `Desconsiderar ${a.targetPunch ? timeOf(a.targetPunch.punchedAt) : "marcação"}`;
  if (a.minutes) return `${ADJUSTMENT_TYPE_LABELS[a.type]} (${duration(a.minutes)})`;
  return ADJUSTMENT_TYPE_LABELS[a.type];
}

/**
 * Espelho de ponto de um colaborador no mês, para o DP: o previsto pela escala,
 * as marcações efetivas (originais menos as desconsideradas, mais as incluídas
 * por ajuste), horas, faltas e o que impede o fechamento. Clicar no dia mostra
 * as marcações originais (NSR, origem, distância da loja) e os ajustes do dia.
 */
export default function TimesheetDetailPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_TIMESHEETS_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const { contractId = "", year: yearParam, month: monthParam } = useParams();
  const year = Number(yearParam);
  const month = Number(monthParam);

  const [sheet, setSheet] = useState<DpTimesheetDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [modal, setModal] = useState<{ mode: TimeAdjustmentModalMode; day: TimesheetDay; punch?: DpPunch; suggested?: number | null } | null>(null);
  const [bulk, setBulk] = useState<{ from: string; to: string } | null>(null);
  const [reasonFor, setReasonFor] = useState<Reason | null>(null);
  const [blocked, setBlocked] = useState<string[] | null>(null);
  const [photoOf, setPhotoOf] = useState<DpPunch | null>(null);

  const load = useCallback(async () => {
    if (!token || !contractId) return;
    try {
      setSheet(await getTimesheetApi(token, contractId, year, month));
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Erro ao carregar o espelho.");
      setSheet(null);
    }
  }, [token, contractId, year, month]);

  useEffect(() => {
    setSheet(null);
    setSelectedDay(null);
    load();
  }, [load]);

  const open = sheet?.status === "ABERTO";
  const name = sheet ? sheet.employee.socialName || sheet.employee.fullName : "";

  const disregarded = useMemo(() => {
    const ids = new Set<string>();
    for (const a of sheet?.adjustments ?? []) {
      if (a.type === "DESCONSIDERAR_MARCACAO" && a.status === "APROVADO" && !a.annulledBy && a.targetPunchId) ids.add(a.targetPunchId);
    }
    return ids;
  }, [sheet]);

  const pending = (sheet?.adjustments ?? []).filter((a) => a.status === "PENDENTE");
  const batches = useMemo(() => {
    const map = new Map<string, DpAdjustment[]>();
    for (const a of sheet?.adjustments ?? []) {
      if (!a.batchId || a.type === "ANULACAO" || a.status !== "APROVADO" || a.annulledBy) continue;
      map.set(a.batchId, [...(map.get(a.batchId) ?? []), a]);
    }
    return Array.from(map.entries());
  }, [sheet]);

  const day = sheet?.days.find((d) => key(d.day) === selectedDay) ?? null;
  const dayPunches = (sheet?.punches ?? []).filter((p) => key(p.localDay) === selectedDay);
  const dayAdjustments = (sheet?.adjustments ?? []).filter((a) => key(a.day) === selectedDay && a.type !== "ANULACAO");

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      toast.success(success);
      await load();
    } catch (e: any) {
      if (e instanceof TimesheetBlockedError) setBlocked(e.blockers);
      else toast.error(e?.message || "Não foi possível concluir.");
    } finally {
      setBusy(false);
    }
  };

  const goMonth = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    navigate(DP_TIMESHEET_PAGE(contractId, d.getFullYear(), d.getMonth() + 1), { replace: true });
  };

  const renderAdjustment = (a: DpAdjustment) => (
    <div key={a.id} className={`dpTkAdj ${a.status === "PENDENTE" ? "dpTkAdj--pending" : ""}`}>
      <div className="dpTkAdj__head">
        <strong>{describeAdjustment(a)}</strong>
        <span className={`dpBadge ${a.annulledBy ? "dpBadge--gray" : ADJUSTMENT_STATUS_BADGE[a.status]}`}>
          {a.annulledBy ? "Anulado" : ADJUSTMENT_STATUS_LABELS[a.status]}
        </span>
      </div>
      <span className="dpMuted">
        {weekdayDate(a.day)} · {ADJUSTMENT_REASON_LABELS[a.reasonCode]} · {a.requestedByEmployee ? "pedido do colaborador" : `lançado por ${a.requestedByUser?.nomeCompleto ?? "DP"}`}
        {a.batchId ? " · em lote" : ""}
      </span>
      {a.reason ? <span>“{a.reason}”</span> : null}
      {a.decisionNote ? (
        <span className="dpMuted">
          Decisão{a.decidedBy ? ` de ${a.decidedBy.nomeCompleto}` : ""}: {a.decisionNote}
        </span>
      ) : null}
      {a.annulledBy?.reason ? <span className="dpMuted">Anulado: {a.annulledBy.reason}</span> : null}
      <div className="dpTkActions">
        {a.attachmentAssetId && token ? (
          <DocumentFileButton token={token} fetchBlob={() => fetchAdjustmentFileApi(token, a.id)} label="Anexo" className="dpBtn dpBtn--secondary dpBtn--small" />
        ) : null}
        {open && a.status === "PENDENTE" ? (
          <>
            <button type="button" className="dpBtn dpBtn--primary dpBtn--small" disabled={busy} onClick={() => run(() => approveAdjustmentApi(token as string, a.id), "Pedido aprovado.")}>
              Aprovar
            </button>
            <button type="button" className="dpBtn dpBtn--danger dpBtn--small" disabled={busy} onClick={() => setReasonFor({ kind: "reject", adjustment: a })}>
              Recusar
            </button>
          </>
        ) : null}
        {open && a.status === "APROVADO" && !a.annulledBy ? (
          <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" disabled={busy} onClick={() => setReasonFor({ kind: "annul", adjustment: a })}>
            Anular
          </button>
        ) : null}
      </div>
    </div>
  );

  if (!token) return null;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Ponto", onClick: () => navigate(DP_TIMESHEETS_PAGE()) },
              { label: name || "Espelho" },
            ]}
            title={name ? `Espelho de ${name}` : "Espelho de ponto"}
            subtitle={
              sheet ? (
                <>
                  {sheet.store?.nomeFantasia ?? "Sem loja"} · {MONTH_LABELS[month - 1]} de {year}
                  {sheet.employee.cpf ? ` · CPF ${formatCpf(sheet.employee.cpf)}` : ""}
                  {sheet.contract.registrationNumber ? ` · Mat. ${sheet.contract.registrationNumber}` : ""}
                </>
              ) : undefined
            }
            onBack={goBack}
            backLabel="Voltar ao ponto"
            actions={
              sheet ? (
                <>
                  <DocumentFileButton
                    token={token}
                    fetchBlob={() => fetchTimesheetPdfApi(token, contractId, year, month)}
                    label="PDF"
                    className="ecoBtn"
                    title="Espelho em PDF, com o rodapé de assinatura"
                  />
                  {open ? (
                    <>
                      <button
                        type="button"
                        className="ecoBtn"
                        disabled={busy}
                        onClick={() => {
                          const first = `${year}-${String(month).padStart(2, "0")}-01`;
                          setBulk({ from: first, to: first });
                        }}
                      >
                        Lançar em lote
                      </button>
                      <button type="button" className="ecoBtn" disabled={busy} onClick={() => run(() => recomputeTimesheetApi(token, contractId, year, month), "Espelho recalculado.")}>
                        Recalcular
                      </button>
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--primary"
                        disabled={busy || !sheet.monthEnded}
                        title={!sheet.monthEnded ? "O espelho só fecha depois que o mês termina." : undefined}
                        onClick={() => run(() => closeTimesheetApi(token, contractId, year, month), "Espelho fechado.")}
                      >
                        Fechar mês
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="ecoBtn"
                      disabled={busy || sheet.payrollClosed}
                      title={sheet.payrollClosed ? "A folha deste mês está fechada. Reabra a folha primeiro." : undefined}
                      onClick={() => setReasonFor({ kind: "reopen" })}
                    >
                      Reabrir
                    </button>
                  )}
                </>
              ) : undefined
            }
          />

          <div className="dpControls">
            <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => goMonth(-1)}>
              ‹ Mês anterior
            </button>
            <strong>
              {MONTH_LABELS[month - 1]} de {year}
            </strong>
            <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => goMonth(1)}>
              Próximo mês ›
            </button>
            {sheet ? (
              <span className={`dpBadge ${sheet.status === "FECHADO" ? "dpBadge--green" : sheet.blockers.length ? "dpBadge--orange" : "dpBadge--blue"}`}>
                {sheet.status === "FECHADO" ? "Fechado" : sheet.blockers.length ? "Aberto com bloqueio" : "Aberto"}
              </span>
            ) : null}
          </div>

          {error ? <p className="dpNotice dpNotice--warning">{error}</p> : null}
          {!sheet && !error ? <div className="dpEmpty">Carregando...</div> : null}

          {sheet ? (
            <>
              {sheet.status === "FECHADO" ? (
                <p className="dpNotice">
                  Fechado{sheet.closedAt ? ` em ${brDateTime(sheet.closedAt)}` : ""}
                  {sheet.closedBy ? ` por ${sheet.closedBy.nomeCompleto}` : ""}. Os totais deste mês já valem na folha.
                </p>
              ) : null}
              <SignatureCard sheet={sheet} onReopen={open || sheet.payrollClosed ? undefined : () => setReasonFor({ kind: "reopen" })} />
              {sheet.reopenedAt ? (
                <p className="dpNotice">
                  Reaberto em {brDateTime(sheet.reopenedAt)}
                  {sheet.reopenedBy ? ` por ${sheet.reopenedBy.nomeCompleto}` : ""}: {sheet.reopenReason}
                </p>
              ) : null}
              {open && sheet.blockers.length ? (
                <div className="dpNotice dpNotice--warning">
                  <strong>Impede o fechamento:</strong> {sheet.blockers.map(describeIssue).join(" · ")}
                </div>
              ) : null}
              {sheet.warnings.length ? <p className="dpNotice">Avisos: {sheet.warnings.map(describeIssue).join(" · ")}</p> : null}
              {open && sheet.provisionedAt && sheet.provisionedAt > sheet.contract.admissionDate && key(sheet.provisionedAt) > `${year}-${String(month).padStart(2, "0")}-01` ? (
                <p className="dpNotice">
                  O acesso ao app foi criado em {formatDateOnly(sheet.provisionedAt)}, depois da admissão. Os dias antes disso entram por "Lançar em lote" com o motivo "Sem acesso ao app".
                </p>
              ) : null}

              <div className="dpTkTotals">
                <div className="dpTkTotal">
                  <span>Trabalhado / previsto</span>
                  <strong>
                    {duration(sheet.workedMinutes)} / {duration(sheet.expectedMinutes)}
                  </strong>
                </div>
                <div className="dpTkTotal">
                  <span>HE dia útil</span>
                  <strong>{duration(sheet.overtimeWorkdayMinutes)}</strong>
                </div>
                <div className="dpTkTotal">
                  <span>HE descanso</span>
                  <strong>{duration(sheet.overtimeRestDayMinutes)}</strong>
                </div>
                <div className="dpTkTotal">
                  <span>Noturno (hora reduzida)</span>
                  <strong>{duration(sheet.nightComputedMinutes)}</strong>
                </div>
                <div className={`dpTkTotal ${sheet.absenceDays ? "dpTkTotal--bad" : ""}`}>
                  <span>Faltas · DSR perdidos</span>
                  <strong>
                    {sheet.absenceDays} · {sheet.dsrLostCount}
                  </strong>
                </div>
                <div className="dpTkTotal">
                  <span>Atrasos e saídas antes</span>
                  <strong>{duration(sheet.lateMinutes + sheet.earlyLeaveMinutes)}</strong>
                </div>
                <div className="dpTkTotal">
                  <span>Dias trabalhados (VT/VR)</span>
                  <strong>{sheet.workedDaysCount}</strong>
                </div>
                <div className="dpTkTotal">
                  <span>Marcações · por ajuste</span>
                  <strong>
                    {sheet.originalPunchCount} · {sheet.adjustedPunchCount}
                  </strong>
                </div>
              </div>

              <div className="dpTkLayout">
                <div className="dpTkDays">
                  {sheet.days
                    .filter((d) => !d.flags.includes("FORA_DO_PERIODO"))
                    .map((d) => {
                      const k = key(d.day);
                      const issue = d.flags.includes("MARCACAO_IMPAR") || (sheet.adjustments.some((a) => a.status === "PENDENTE" && key(a.day) === k));
                      const rest = REST.has(d.kind) && !d.effectivePunches.length;
                      return (
                        <button
                          key={d.id ?? k}
                          type="button"
                          className={`dpTkDay ${rest ? "dpTkDay--rest" : ""} ${d.kind === "FALTA" ? "dpTkDay--absence" : issue ? "dpTkDay--issue" : ""} ${selectedDay === k ? "dpTkDay--selected" : ""}`}
                          onClick={() => setSelectedDay(selectedDay === k ? null : k)}
                        >
                          <span className="dpTkDay__date">{weekdayDate(d.day)}</span>
                          <span className="dpTkDay__expected">
                            {DAY_KIND_LABELS[d.kind]}
                            {d.dsrLost ? " · DSR perdido" : ""}
                            <small>
                              {d.startMinute !== null && (d.kind === "TRABALHO" || d.kind === "FALTA")
                                ? `${SHIFT[d.shift ?? ""] ?? ""} ${hhmm(d.startMinute)}–${hhmm(d.endMinute)}`
                                : d.expectedMinutes
                                  ? `${duration(d.expectedMinutes)} (padrão)`
                                  : ""}
                            </small>
                          </span>
                          <span>
                            <span className="dpTkChips">
                              {d.effectivePunches.map((p) => (
                                <span key={p.at} className={`dpTkChip ${p.source === "AJUSTE" ? "dpTkChip--adj" : ""} ${p.outsideStore ? "dpTkChip--warn" : ""}`}>
                                  {timeOf(p.at)}
                                  {p.source === "AJUSTE" ? "*" : ""}
                                </span>
                              ))}
                            </span>
                            {d.flags
                              .filter((f) => DAY_FLAG_LABELS[f])
                              .map((f) => (
                                <span key={f} className={`dpTkFlag ${INFO_FLAGS.has(f) ? "dpTkFlag--info" : ""}`}>
                                  {DAY_FLAG_LABELS[f]}
                                </span>
                              ))}
                          </span>
                          <span className="dpTkDay__nums dpTkNum">
                            {d.workedMinutes ? duration(d.workedMinutes) : "—"}
                            <small>
                              {d.overtimeWorkdayMinutes + d.overtimeRestDayMinutes ? `HE ${duration(d.overtimeWorkdayMinutes + d.overtimeRestDayMinutes)}` : ""}
                              {d.nightComputedMinutes ? ` · not. ${duration(d.nightComputedMinutes)}` : ""}
                            </small>
                          </span>
                          <span className="dpTkDay__nums dpTkNum">
                            {d.lateMinutes + d.earlyLeaveMinutes ? `−${duration(d.lateMinutes + d.earlyLeaveMinutes)}` : ""}
                            <small>{d.breakTakenMinutes ? `intervalo ${duration(d.breakTakenMinutes)}` : ""}</small>
                          </span>
                        </button>
                      );
                    })}
                  <p className="dpMuted">* Marcação incluída por ajuste. Contorno laranja: marcação longe da loja.</p>
                </div>

                <aside className="dpTkSide">
                  {day ? (
                    <>
                      <div className="dpTkAdj__head">
                        <h3>{weekdayDate(day.day)}</h3>
                        <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setSelectedDay(null)}>
                          Pendências do mês
                        </button>
                      </div>
                      <span className="dpMuted">
                        {DAY_KIND_LABELS[day.kind]}
                        {day.startMinute !== null ? ` · previsto ${hhmm(day.startMinute)}–${hhmm(day.endMinute)} (${duration(day.expectedMinutes)})` : ""}
                        {day.expectedSource === "PADRAO" ? " · jornada padrão (sem escala publicada)" : ""}
                      </span>

                      <strong>Marcações originais</strong>
                      {dayPunches.length ? (
                        dayPunches.map((p) => (
                          <div key={p.id} className={`dpTkPunchRow ${disregarded.has(p.id) ? "dpTkPunchRow--off" : ""}`}>
                            <span>
                              <strong>{timeOf(p.punchedAt)}</strong> · nº {p.nsr}
                              <small>
                                {p.source === "MOBILE" ? "Celular" : "Navegador"}
                                {p.ipAddress ? ` · IP ${p.ipAddress}` : ""}
                                {p.distanceToStoreMeters !== null ? ` · ${formatMeters(p.distanceToStoreMeters)} da loja` : p.latitude === null ? " · sem localização" : " · loja sem posição"}
                                {p.outsideStore ? " (fora do raio)" : ""}
                              </small>
                              <small>código {p.hash}</small>
                            </span>
                            <span className="dpTkPunchRow__actions">
                              {p.hasPhoto ? (
                                <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setPhotoOf(p)}>
                                  Ver foto
                                </button>
                              ) : null}
                              {open && !disregarded.has(p.id) ? (
                                <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setModal({ mode: "DESCONSIDERAR_MARCACAO", day, punch: p })}>
                                  Desconsiderar
                                </button>
                              ) : null}
                            </span>
                          </div>
                        ))
                      ) : (
                        <span className="dpMuted">Nenhuma marcação original.</span>
                      )}

                      {open ? (
                        <div className="dpTkActions">
                          <button
                            type="button"
                            className="dpBtn dpBtn--primary dpBtn--small"
                            onClick={() => {
                              const marks = day.effectivePunches.length;
                              const suggested = day.startMinute !== null ? [day.startMinute, null, null, day.endMinute][marks] ?? null : null;
                              setModal({ mode: "INCLUSAO_MARCACAO", day, suggested });
                            }}
                          >
                            Incluir marcação
                          </button>
                          {day.kind === "TRABALHO" || day.kind === "FALTA" ? (
                            <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setBulk({ from: key(day.day), to: key(day.day) })}>
                              Dia inteiro
                            </button>
                          ) : null}
                          {day.kind === "FALTA" ? (
                            <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setModal({ mode: "ABONO_FALTA", day })}>
                              Abonar falta
                            </button>
                          ) : null}
                          {day.lateMinutes + day.earlyLeaveMinutes > 0 ? (
                            <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setModal({ mode: "ABONO_ATRASO", day })}>
                              Abonar atraso
                            </button>
                          ) : null}
                        </div>
                      ) : null}

                      <strong>Ajustes do dia</strong>
                      {dayAdjustments.length ? dayAdjustments.map(renderAdjustment) : <span className="dpMuted">Nenhum ajuste.</span>}
                    </>
                  ) : (
                    <>
                      <div className="dpTkAdj__head">
                        <h3>Pedidos pendentes ({pending.length})</h3>
                        {open && pending.length > 1 ? (
                          <button
                            type="button"
                            className="dpBtn dpBtn--primary dpBtn--small"
                            disabled={busy}
                            onClick={() =>
                              run(async () => {
                                const res = await approveManyAdjustmentsApi(token, pending.map((a) => a.id));
                                if (res.failed.length) toast.warn(`${res.failed.length} não puderam ser aprovados.`);
                              }, "Pedidos aprovados.")
                            }
                          >
                            Aprovar todos
                          </button>
                        ) : null}
                      </div>
                      {pending.length ? pending.map(renderAdjustment) : <span className="dpMuted">Nenhum pedido aguardando. Clique num dia para ver as marcações e lançar ajustes.</span>}

                      {batches.length ? (
                        <>
                          <strong>Lotes lançados</strong>
                          {batches.map(([batchId, items]) => (
                            <div key={batchId} className="dpTkAdj">
                              <div className="dpTkAdj__head">
                                <span>
                                  {items.length} marcação(ões) · {Array.from(new Set(items.map((i) => weekdayDate(i.day)))).length} dia(s)
                                </span>
                                {open ? (
                                  <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => setReasonFor({ kind: "annulBatch", batchId, count: items.length })}>
                                    Anular lote
                                  </button>
                                ) : null}
                              </div>
                              <span className="dpMuted">
                                {ADJUSTMENT_REASON_LABELS[items[0].reasonCode]} · {items[0].requestedByEmployee ? "pedido do colaborador" : items[0].requestedByUser?.nomeCompleto ?? "DP"}
                              </span>
                            </div>
                          ))}
                        </>
                      ) : null}
                    </>
                  )}
                </aside>
              </div>
            </>
          ) : null}
        </div>
      </main>

      {modal && sheet ? (
        <TimeAdjustmentModal
          token={token}
          contractId={contractId}
          day={modal.day}
          mode={modal.mode}
          punch={modal.punch}
          suggestedMinute={modal.suggested}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      ) : null}

      {bulk && sheet ? (
        <BulkTimeAdjustmentModal
          token={token}
          contractId={contractId}
          employeeName={name}
          defaultFrom={bulk.from}
          defaultTo={bulk.to}
          suggestNoCredential={!!sheet.provisionedAt && key(sheet.provisionedAt) > bulk.from}
          onClose={() => setBulk(null)}
          onSaved={() => {
            setBulk(null);
            load();
          }}
        />
      ) : null}

      {photoOf && token ? (
        <PunchPhotoModal
          token={token}
          punchId={photoOf.id}
          title={`${name} · ${timeOf(photoOf.punchedAt)}`}
          details={[
            `${weekdayDate(photoOf.localDay)} · marcação nº ${photoOf.nsr}`,
            photoOf.distanceToStoreMeters !== null
              ? `${formatMeters(photoOf.distanceToStoreMeters)} da loja${photoOf.outsideStore ? " (fora do raio)" : ""}`
              : photoOf.latitude === null
                ? "Sem localização"
                : "Loja sem posição cadastrada",
          ]}
          onClose={() => setPhotoOf(null)}
        />
      ) : null}

      {reasonFor ? (
        <ReasonModal
          title={
            reasonFor.kind === "reject"
              ? "Recusar o pedido"
              : reasonFor.kind === "annul"
                ? "Anular o ajuste"
                : reasonFor.kind === "annulBatch"
                  ? `Anular o lote (${reasonFor.count} marcações)`
                  : "Reabrir o espelho"
          }
          description={
            reasonFor.kind === "reject"
              ? "O colaborador vê este motivo no portal."
              : reasonFor.kind === "reopen"
                ? "O espelho volta a ser recalculado e a assinatura do colaborador, se houver, deixa de valer para a versão nova."
                : "O ajuste continua no histórico; só deixa de valer no espelho."
          }
          confirmLabel={reasonFor.kind === "reject" ? "Recusar" : reasonFor.kind === "reopen" ? "Reabrir" : "Anular"}
          onClose={() => setReasonFor(null)}
          onConfirm={async (reason) => {
            if (reason.length < 5) throw new Error("Explique o motivo (mínimo de 5 caracteres).");
            const target = reasonFor;
            if (target.kind === "reject") await rejectAdjustmentApi(token, target.adjustment.id, reason);
            else if (target.kind === "annul") await annulAdjustmentApi(token, target.adjustment.id, reason);
            else if (target.kind === "annulBatch") await annulBatchApi(token, target.batchId, reason);
            else await reopenTimesheetApi(token, contractId, year, month, reason);
            setReasonFor(null);
            toast.success(target.kind === "reject" ? "Pedido recusado." : target.kind === "reopen" ? "Espelho reaberto." : "Anulado.");
            await load();
          }}
        />
      ) : null}

      {blocked ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">O espelho não pode ser fechado</h2>
              <button className="dpModal__close" onClick={() => setBlocked(null)}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <ul className="dpRuleList">
                {blocked.map((b) => (
                  <li key={b}>{describeIssue(b)}</li>
                ))}
              </ul>
              <p className="dpFormHint">Resolva com ajustes (incluir a marcação que falta) e decida os pedidos pendentes.</p>
            </div>
            <footer className="dpModal__footer">
              <button type="button" className="dpBtn dpBtn--primary" onClick={() => setBlocked(null)}>
                Ok
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Assinatura do colaborador: situação, data, hash curto e o histórico (aceites e contestações). */
function SignatureCard({ sheet, onReopen }: { sheet: DpTimesheetDetail; onReopen?: () => void }) {
  const sig = sheet.signature;
  if (!sig || (sig.status === "NAO_FECHADO" && !sheet.signatureHistory?.length)) return null;
  return (
    <div className={`dpNotice ${sig.status === "CONTESTADO" ? "dpNotice--warning" : ""}`}>
      <strong>Assinatura do colaborador: </strong>
      <span className={`dpBadge ${SIGNATURE_BADGE[sig.status]}`}>{SIGNATURE_LABELS[sig.status]}</span>{" "}
      {sig.status === "ASSINADO" && sig.signedAt ? <>em {brDateTime(sig.signedAt)} (hash {sig.hashShort}).</> : null}
      {sig.status === "PENDENTE" || (sig.status === "DESATUALIZADO" && sig.canSign) ? (
        <>
          aguardando há {sig.daysPending} dia(s){sig.overdue ? " — passou do prazo (só informativo, não bloqueia a folha)" : ""}.
        </>
      ) : null}
      {sig.status === "DESATUALIZADO" && !sig.canSign ? <>o espelho foi reaberto depois da assinatura; ao fechar de novo, o colaborador assina a nova versão.</> : null}
      {sig.status === "CONTESTADO" && sig.lastContest ? (
        <>
          <div style={{ marginTop: 6 }}>
            Contestado em {brDateTime(sig.lastContest.createdAt)}: <em>"{sig.lastContest.text}"</em>
          </div>
          <div style={{ marginTop: 6 }}>
            Contestado — reabra para corrigir. A contestação não impede a folha.{" "}
            {onReopen ? (
              <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={onReopen}>
                Reabrir o espelho
              </button>
            ) : null}
          </div>
        </>
      ) : null}
      {sheet.signatureHistory?.length ? (
        <details style={{ marginTop: 6 }}>
          <summary>Histórico ({sheet.signatureHistory.length})</summary>
          <ul className="dpRuleList">
            {sheet.signatureHistory.map((h) => (
              <li key={h.createdAt + h.kind}>
                {brDateTime(h.createdAt)} — {h.kind === "ACEITE" ? "Assinou" : "Contestou"} (hash {h.hashShort}
                {h.ipAddress ? `, IP ${h.ipAddress}` : ""}){h.text ? `: ${h.text}` : ""}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
